import { DatePipe } from '@angular/common';
import { ChangeDetectorRef, Component, Input } from '@angular/core';

import {
    COACHING_WIDGET_PERIOD_TYPES,
    goalVariancePercent,
    MetricDetailPerformanceData,
    Opportunity,
    PerformanceData,
    targetVariancePercent,
} from '../../coaching-insight/oa/models/oa.model';
import { PeriodType } from '../../widget/date-range.widget.model';
import { TranslationKeyMap } from '../coaching-commitment.i18n';

@Component({
    selector: 'goal-grid-commitment',
    templateUrl: './goal-grid.commitment.component.html',
    providers: [DatePipe],
})
export class GoalGridCommitmentComponent {
    @Input() data: MetricDetailPerformanceData[];
    @Input() activeMetric: Opportunity;
    @Input() periods: any[];
    @Input() componentLength = 0;
    @Input() metricNames: string[] = [];

    colSpan = 1;

    PeriodType = PeriodType;

    COACHING_WIDGET_PERIOD_TYPES = COACHING_WIDGET_PERIOD_TYPES;

    periodNow: string;
    periodPrevious: string;
    periodPreviousPrevious: string;

    localData: any[];
    periodsData: any[];

    i18n = TranslationKeyMap;

    constructor(
        private readonly datePipe: DatePipe,
        private readonly cd: ChangeDetectorRef,
    ) {}

    // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
    ngOnChanges(args) {
        // eslint-disable-next-line no-prototype-builtins
        if (args.hasOwnProperty('data') || args.hasOwnProperty('periods')) {
            this.setup();
        }
    }

    // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
    setup() {
        const colMultiplier = this.calculateColMultiplier();

        if (!this.localData || !this.localData.length) return;

        this.localData = this.localData.map((l: PerformanceData) => {
            this.getPercentages(l);

            if (l.trendName) {
                l = this.getTrendName(l);
            }

            l.gap = this.calculateGap(l);
            l.opportunityThreshold = this.getThreshold();
            return l;
        });

        if (this.localData.length < colMultiplier) {
            this.setMetricNames(colMultiplier);
        }

        this.calculateColSpan();

        this.cd.detectChanges();
    }

    // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
    getThreshold() {
        return this.activeMetric.oaThresholdValue * 100;
    }

    getTrendName(l: PerformanceData): PerformanceData {
        const td = l.trend.split(' ');
        if (td.length) {
            if (td?.[1] != null) {
                l.arrow = td[1].toLowerCase();
            }
            if (td?.[0] != null) {
                l.arrowColour = td[0].toLowerCase();
            }
        }
        return l;
    }

    // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
    getPercentages(l: PerformanceData) {
        if (l.actual) {
            l.targetPercent = targetVariancePercent(
                l.actual,
                l.target,
                l.rankOrder === 0 ? -1 : 1,
            );
            l.goalPercent = goalVariancePercent(
                l.actual,
                l.goal,
                l.rankOrder === 0 ? -1 : 1,
            );
        }
    }

    calculateColMultiplier(): number {
        let colMultiplier = 3;

        switch (this.componentLength) {
            case 2:
                this.periodsData = this.periods.splice(0, 3); // get first 2 periods
                this.localData = this.data.splice(0, 9); // get first 6 data
                colMultiplier = 1;
                break;
            case 3:
                this.periodsData = [this.periods[0]]; // get first period
                this.metricNames = this.metricNames.splice(0, 4); // metric names include the parent metric
                this.localData = this.data.splice(0, 4); // get first 5 (includes the primary metric)
                colMultiplier = 1;
                break;
            case 4:
                this.periodsData = [this.periods[0]]; // get first period
                this.metricNames = this.metricNames.splice(0, 5);
                this.localData = this.data.splice(0, 4); // get first 5 (includes the primary metric)
                colMultiplier = 1;
                break;
            case 5:
                this.periodsData = [this.periods[0]]; // get first period
                this.metricNames = this.metricNames.splice(0, 6);
                this.localData = this.data.splice(0, 5); // get first 5 (includes the primary metric)
                colMultiplier = 1;
                break;
            default:
                this.localData = this.data; // no change
                this.periodsData = this.periods;
        }
        return (
            colMultiplier *
            (this.componentLength ? this.componentLength + 1 : 1)
        );
    }

    // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
    calculateColSpan() {
        if (!this.periodsData) {
            return;
        }
        this.colSpan =
            this.localData.length / this.periodsData.length > 1
                ? this.localData.length / this.periodsData.length
                : 1;
    }

    calculateGap(l: PerformanceData): number {
        return (
            (((l.actual - l.target) / l.target) * (l.rankOrder === 0 ? -1 : 1) -
                this.activeMetric.oaThresholdValue) *
            l.target
        );
    }

    // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
    setMetricNames(multiplier) {
        const len = multiplier - this.localData.length;

        for (let i = 0; i < len; i++) {
            this.localData.push({
                metricShortName: this.localData[i].metricShortName,
            });
        }
    }

    // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
    getPeriodText(period) {
        const startdate = period.startDate;
        const enddate = period.endDate;

        return `${this.datePipe.transform(
            startdate,
            'MMM d',
        )} - ${this.datePipe.transform(enddate, 'MMM d')}`;
    }
}
